import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router'
import { useAuthStore } from '@/stores/authStore'
import { notify } from '@/utils/notify'
import { CircleStackIcon } from '@heroicons/react/24/outline'
import { getCurrentUser, getApiBaseUrl } from '@/services/apiClient'

async function exchangeCodeForToken(code: string): Promise<{ access_token: string; refresh_token?: string }> {
  const response = await fetch(`${getApiBaseUrl()}/api/auth/google/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { detail?: string }
    throw new Error(errorData.detail || 'Failed to exchange authorization code')
  }
  return response.json() as Promise<{ access_token: string; refresh_token?: string }>
}

export function OAuthCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const authStore = useAuthStore()
  const { setUser, setToken } = authStore
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Prevent double execution in StrictMode or double mounting
    if (hasProcessed.current) {
      return
    }

    const handleCallback = async () => {
      hasProcessed.current = true
      try {
        // Support both flows: direct token (legacy) and authorization code exchange
        let token = searchParams.get('token')
        let refreshToken = searchParams.get('refresh_token')
        const code = searchParams.get('code')
        const error = searchParams.get('error')

        if (error) {
          const errorMessage = error === 'oauth_failed' ? 'Google authentication failed' :
                             error === 'auth_failed' ? 'Authentication error' :
                             error === 'email_exists' ? 'Email already linked to another account' :
                             'Authentication failed'
          notify.error(errorMessage)
          navigate('/login')
          return
        }

        // Exchange authorization code for token if using code flow
        if (!token && code) {
          const exchangeResult = await exchangeCodeForToken(code)
          token = exchangeResult.access_token
          refreshToken = exchangeResult.refresh_token || null
        }

        if (token) {
          // Store tokens in localStorage with correct keys
          localStorage.setItem('access_token', token)
          // Also store as 'token' for compatibility
          localStorage.setItem('token', token)
          if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken)
          }

          setToken(token)

          // Clean URL by removing token parameters
          window.history.replaceState({}, document.title, window.location.pathname)

          try {
            // Fetch user profile using the token
            const { data: userProfile } = await getCurrentUser({ throwOnError: true })
            
            // Store user data - map UserInfo to User type
            const user = {
              id: userProfile.id || '',
              username: userProfile.username || userProfile.email || '',
              email: userProfile.email || undefined,
              name: userProfile.fullName || userProfile.email?.split('@')[0] || '',
              role: 'user' as const,
              createdAt: new Date().toISOString(),
              authProvider: 'google' as const
            }
            localStorage.setItem('user', JSON.stringify(user))
            setUser(user)

            // Set refreshToken in store if available
            if (refreshToken) {
              useAuthStore.setState({ refreshToken })
            }
            
            // Fetch user settings
            try {
              const { useSettingsStore } = await import('@/stores/settingsStore')
              await useSettingsStore.getState().fetchSettings()
              console.log('✅ [OAuth] User settings fetched after SSO login')
            } catch (settingsError) {
              console.error('Failed to fetch settings after SSO login:', settingsError)
            }

            // Start outdated agents monitoring after SSO login
            try {
              const { useOutdatedAgentsStore } = await import('@/stores/outdatedAgentsStore')
              useOutdatedAgentsStore.getState().startMonitoring()
              console.log('✅ [OAuth] Outdated agents monitoring started after SSO login')
            } catch (monitoringError) {
              console.error('Failed to start outdated agents monitoring after SSO login:', monitoringError)
            }

            // Start pending invitations monitoring after SSO login
            try {
              const { usePendingInvitationsStore } = await import('@/stores/pendingInvitationsStore')
              usePendingInvitationsStore.getState().startMonitoring()
              console.log('✅ [OAuth] Pending invitations monitoring started after SSO login')
            } catch (monitoringError) {
              console.error('Failed to start pending invitations monitoring after SSO login:', monitoringError)
            }

            // Check for stored redirect path or use default
            const storedRedirect = sessionStorage.getItem('oauth_redirect')
            if (storedRedirect) {
              sessionStorage.removeItem('oauth_redirect')
              navigate(storedRedirect)
            } else {
              navigate('/')
            }
          } catch (profileError) {
            console.error('Failed to fetch user profile:', profileError)
            notify.error('Failed to load user profile')
            navigate('/login')
          }
        } else {
          notify.error('Invalid authentication response')
          navigate('/login')
        }
      } catch (error) {
        console.error('OAuth callback error:', error)
        notify.error('Authentication failed')
        navigate('/login')
      }
    }

    void handleCallback()
  }, [searchParams, navigate, location, setUser, setToken])

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full">
              <CircleStackIcon className="w-10 h-10 text-primary-600" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            Authenticating...
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Please wait while we complete your sign-in
          </p>
        </div>
      </div>
    </div>
  )
}